import { useState, useEffect } from "react";

import { css } from "@compiled/react";

import AtlasKitProgressBar from "@atlaskit/progress-bar";
import { R400 } from "@atlaskit/theme/colors";

import {
    ProvisioningStatus,
    TaskName
} from "@/libraries/bxp-web-stargate-client/src";

import { useCloudStatus } from "../hooks";

const progressStyle = css({
    textAlign: "center",
    display: "flex",
    padding: "0",
    flexDirection: "column",
    justifyContent: "center",
    alignSelf: "center",
    width: "100%",
    maxWidth: "400px",
    margin: "16px",
    alignItems: "center"
});

interface ProvisioningProgressBarProps {
    provisioningStep?: string;
    provisioningStatus?: ProvisioningStatus;
}

/**
 * Supported provisioning steps and their corresponding progress values
 */
const SUPPORTED_STEPS: Record<string, number> = {
    [TaskName.ACTIVATE]: 0.2, // 20%
    [TaskName.WAIT_FOR_ACTIVATION]: 0.35, // 35%
    [TaskName.WAIT_FOR_JIRA]: 0.5, // 50%
    [TaskName.CREATE_JIRA_PROJECT]: 0.8, // 80% - often the final step
    [TaskName.ONBOARD_JPD_PROJECT]: 0.8 // 80% - often the final step, only for JPD
};

/**
 * Maps provisioning steps to progress percentages
 * Returns null if step is unsupported
 */
const getStepProgress = (step?: string): number | null => {
    if (!step) {
        return null;
    }

    if (!Object.keys(SUPPORTED_STEPS).includes(step)) {
        return null;
    }

    return SUPPORTED_STEPS[step] ?? null;
};

/**
 * Calculates progress based on provisioning step and status
 * Returns null if step is unknown and should not update progress
 */
const calculateProgress = (
    provisioningStep?: string,
    provisioningStatus?: ProvisioningStatus
): number | null => {
    // If provisioning is completed, show 100%
    if (provisioningStatus === ProvisioningStatus.PROVISIONING_COMPLETED) {
        return 1;
    }

    // If provisioning failed or aborted, show 100% (red bar will be shown)
    if (
        provisioningStatus === ProvisioningStatus.PROVISIONING_FAILED ||
        provisioningStatus === ProvisioningStatus.PROVISIONING_ABORTED
    ) {
        return 1;
    }

    // Use step-based progress only
    if (provisioningStep) {
        return getStepProgress(provisioningStep);
    }

    // No step information available yet
    return 0;
};

export const ProvisioningProgressBar = ({
    provisioningStep,
    provisioningStatus
}: ProvisioningProgressBarProps) => {
    const { createCloudStatus } = useCloudStatus();
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const newProgress = calculateProgress(
            provisioningStep,
            provisioningStatus
        );

        if (newProgress) {
            setProgress(newProgress);
        }
    }, [provisioningStep, provisioningStatus]);

    const isTimeout =
        createCloudStatus === "timeout" ||
        provisioningStatus === ProvisioningStatus.PROVISIONING_FAILED ||
        provisioningStatus === ProvisioningStatus.PROVISIONING_ABORTED;

    // Only show timeout state if provisioning has actually started
    const hasProvisioningStarted =
        provisioningStep !== undefined || provisioningStatus !== undefined;
    const shouldShowTimeout = isTimeout && hasProvisioningStarted;

    return (
        <div css={progressStyle}>
            <AtlasKitProgressBar
                testId={
                    shouldShowTimeout ? "progress-timeout" : "progress-pending"
                }
                value={shouldShowTimeout ? 1 : progress}
                theme={
                    shouldShowTimeout
                        ? (getTokens, props) => {
                              const tokens = getTokens(props);
                              return {
                                  ...tokens,
                                  bar: {
                                      ...tokens.bar,
                                      background: R400
                                  }
                              };
                          }
                        : undefined
                }
            />
        </div>
    );
};

export default ProvisioningProgressBar;
